<template>
  <div class="sa-details">
    <v-row align="center">
      <v-col>
        <v-breadcrumbs :items="breadcrumb" large></v-breadcrumbs>
      </v-col>
      <v-col class="text-right">
        <v-btn color="warning" dark class="mr-4" @click="retrieveCreds">
          <v-icon left>vpn_key</v-icon>
          <span>Credentials</span>
        </v-btn>
        <v-btn color="primary" icon dark class="mr-4" @click="refresh">
          <v-icon>refresh</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-divider></v-divider>
    <v-banner color="error" dark v-if="fetchOneError">
      <v-icon slot="icon" color="white" size="36">mdi-wifi-strength-alert-outline</v-icon>
      {{ errorDisplay(fetchOneError) }}
      <template v-slot:actions>
        <v-btn text color="dark" @click="refresh">Retry</v-btn>
      </template>
    </v-banner>

    <v-tabs v-model="tab" background-color="primary" dark icons-and-text grow>
      <v-tab href="#tab-settings">
        <span class="mt-2">Settings</span>
        <v-icon>settings</v-icon>
      </v-tab>
      <v-tab-item value="tab-settings" class="grey lighten-5">
        <v-container>
          <ServiceAccountEditionForm
            :uuid="serviceAccount ? serviceAccount.uuid : ''"
            :id="serviceAccount ? serviceAccount.application_id: ''"
            :name="serviceAccount ? serviceAccount.application_name: ''"
            :description="serviceAccount ? serviceAccount.application_description: ''"
            :rootUrl="serviceAccount ? serviceAccount.root_url: ''"
            :redirectUris="serviceAccount ? serviceAccount.redirect_uris: []"
            :webOrigins="serviceAccount ? serviceAccount.web_origins: []"
            v-on:save="updateServiceAccount"
            ref="serviceAccountEditionForm"
          />
        </v-container>
      </v-tab-item>
      <v-tab href="#tab-scopes">
        <span class="mt-2">Scopes</span>
        <v-icon>pages</v-icon>
      </v-tab>
      <v-tab-item value="tab-scopes" class="grey lighten-5">
        <v-container fluid>
          <ServiceAccountScopesPicker
            :availableScopes="availableScopes"
            :optionalScopes="optionalScopes"
            :defaultScopes="defaultScopes"
            v-on:AddDefault="addDefaultScopeRequest"
            v-on:addOptional="addOptionalScopeRequest"
            v-on:removeDefault="removeDefaultScopeRequest"
            v-on:removeOptional="removeOptionalScopeRequest"
          />
        </v-container>
      </v-tab-item>
      <v-tab href="#tab-service-account-roles">
        <span class="mt-2">Service Account Roles</span>
        <v-icon>policy</v-icon>
      </v-tab>
      <v-tab-item value="tab-service-account-roles" class="grey lighten-5">
        <v-container>
          <ServiceAccountRolesPicker
            :currentRoles="serviceAccountRoles"
            :availableRoles="serviceAccountAvailableRoles"
            v-on:addAvailable="addAvailableRole"
            v-on:removeCurrent="removeCurrentRole"
          />
        </v-container>
      </v-tab-item>
    </v-tabs>
    <v-snackbar v-model="snackbar" top>
      {{ errorMessage }}
      <v-btn color="red" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
    <CredentialsDialog
      :dialog="credentialsDialog"
      :clientId="serviceAccount ? serviceAccount.application_id: ''"
      :clientSecret="clientSecret"
      v-on:close="closeCreds"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import mixin from "@/mixins";
import ServiceAccountEditionForm from "@/components/Admin/ServiceAccounts/ServiceAccountEditionForm";
import CredentialsDialog from "@/components/Admin/ServiceAccounts/CredentialsDialog";
import ServiceAccountScopesPicker from "@/components/Admin/ServiceAccounts/ServiceAccountScopesPicker";
import ServiceAccountRolesPicker from "@/components/Admin/ServiceAccounts/ServiceAccountRolesPicker";

export default {
  mixins: [mixin],
  components: {
    ServiceAccountEditionForm,
    CredentialsDialog,
    ServiceAccountScopesPicker,
    ServiceAccountRolesPicker
  },
  data() {
    return {
      serviceAccountUuid: this.$route.params.serviceAccountId,
      serviceAccount: null,
      credentialsDialog: false,
      clientSecret: "",
      swaggerDialog: false,
      snackbar: false,
      errorMessage: "",
      tab: null,
      defaultScopes: [],
      optionalScopes: [],
      availableScopes: [],
      serviceAccountRoles: [],
      serviceAccountAvailableRoles: []
    };
  },
  computed: {
    ...mapState({
      fetchOneError: state => state.serviceAccounts.fetchOne.error,
      fetchOneLoading: state => state.serviceAccounts.fetchOne.pending,
      credsLoading: state => state.serviceAccounts.retrieveCredentials.pending
    }),
    breadcrumb() {
      return [
        {
          text: "Service Accounts",
          disabled: false,
          href: "#/admin/service-accounts"
        },
        {
          text: this.serviceAccount
            ? this.serviceAccount.application_id
            : "unknown",
          disabled: true
        }
      ];
    }
  },
  methods: {
    ...mapActions("serviceAccounts", {
      fetchOne: "fetchOne",
      updateOne: "updateOne",
      retrieveCredentials: "retrieveCredentials",
      getDefaultScopes: "getDefaultScopes",
      addDefaultScope: "addDefaultScope",
      removeDefaultScope: "removeDefaultScope",
      getOptionalScopes: "getOptionalScopes",
      addOptionalScope: "addOptionalScope",
      removeOptionalScope: "removeOptionalScope",
      fetchAllServiceAccountRoles: "fetchAllServiceAccountRoles",
      fetchAllServiceAccountAvailableRoles:
        "fetchAllServiceAccountAvailableRoles",
      addServiceAccountRoles: "addServiceAccountRoles",
      removeServiceAccountRoles: "removeServiceAccountRoles"
    }),
    ...mapActions("scopes", { fetchAllScopes: "fetchAll" }),
    refresh() {
      this.fetchOne({ uuid: this.serviceAccountUuid })
        .then(serviceAccount => {
          this.serviceAccount = serviceAccount;
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
      this.refreshScopes();
      this.refreshRoles();
    },
    refreshScopes() {
      this.getDefaultScopes({ uuid: this.serviceAccountUuid })
        .then(scopes => {
          this.defaultScopes = scopes;
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
      this.getOptionalScopes({ uuid: this.serviceAccountUuid })
        .then(scopes => {
          this.optionalScopes = scopes;
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
      this.fetchAllScopes()
        .then(scopes => {
          this.availableScopes = scopes;
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    refreshRoles() {
      this.fetchAllServiceAccountRoles({ uuid: this.serviceAccountUuid })
        .then(roles => {
          this.serviceAccountRoles = roles;
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
      this.fetchAllServiceAccountAvailableRoles({
        uuid: this.serviceAccountUuid
      })
        .then(roles => {
          this.serviceAccountAvailableRoles = roles;
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    updateServiceAccount(id, name, description) {
      this.updateOne({
        uuid: this.$route.params.serviceAccountId,
        id: id,
        name: name,
        description: description
      })
        .then(() => {
          this.refresh();
          this.$refs.serviceAccountEditionForm.saveCallback();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    retrieveCreds() {
      this.retrieveCredentials({ uuid: this.$route.params.serviceAccountId })
        .then(response => {
          this.clientSecret = response.client_secret;
          this.credentialsDialog = true;
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    closeCreds() {
      this.credentialsDialog = false;
      this.clientSecret = "";
    },
    openSwagger() {
      this.swaggerDialog = true;
    },
    closeSwagger() {
      this.swaggerDialog = false;
    },
    addDefaultScopeRequest(scopeUuid) {
      this.addDefaultScope({
        uuid: this.serviceAccountUuid,
        scopeUuid: scopeUuid
      })
        .then(() => {
          this.refreshScopes();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    addOptionalScopeRequest(scopeUuid) {
      this.addOptionalScope({
        uuid: this.serviceAccountUuid,
        scopeUuid: scopeUuid
      })
        .then(() => {
          this.refreshScopes();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    removeDefaultScopeRequest(scopeUuid) {
      this.removeDefaultScope({
        uuid: this.serviceAccountUuid,
        scopeUuid: scopeUuid
      })
        .then(() => {
          this.refreshScopes();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    removeOptionalScopeRequest(scopeUuid) {
      this.removeOptionalScope({
        uuid: this.serviceAccountUuid,
        scopeUuid: scopeUuid
      })
        .then(() => {
          this.refreshScopes();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    addAvailableRole(uuid, name) {
      this.addServiceAccountRoles({
        uuid: this.serviceAccountUuid,
        roles: [{ uuid: uuid, name: name }]
      })
        .then(() => {
          this.refreshRoles();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    removeCurrentRole(uuid, name) {
      this.removeServiceAccountRoles({
        uuid: this.serviceAccountUuid,
        roles: [{ uuid: uuid, name: name }]
      })
        .then(() => {
          this.refreshRoles();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    }
  },
  created() {
    this.refresh();
  }
};
</script>

<style>
</style>